import { RegisterScreen } from '@/components/RegisterScreen';
import { StatusPill, formatDate, formatNumber } from '@/components/live';
import { YIELD_BASES, YIELD_FORMULAS, yieldFields } from '@/screens/parts/HarvestParts';
import { YieldStatementSheet } from '@/screens/parts/YieldStatementSheet';

interface Row {
  id: string; harvest_code: string | null; plot_id: string; plot_code: string | null; farmer_name: string | null; farmer_code: string | null;
  species_label: string | null; harvest_date: string; trees_harvested: number; status: string;
  yield_basis: string | null; yield_formula: string; yield_lines: Array<Record<string, unknown>>;
  yield_log_count: number | null; yield_volume_m3: number | null; yield_weight_t: number | null;
  estimated_volume: number | null; volume_unit_label: string | null;
}

/**
 * What each harvest yielded (owner, 2026-09-27: "Yield Statement summary? (some are based on
 * tonnage, some are based on M3 - log wise)"): log by log in cubic metres, or load by load in
 * tonnes, against the trees felled. The Statement tab is the sheet to print and sign.
 */
export default function YieldStatementScreen() {
  return (
    <RegisterScreen<Row>
      plotFilter canAdd={false}
      extraTab={(row) => ({ label: 'Statement', content: <YieldStatementSheet harvest={row} /> })}
      addLabel="" exportName="yield-statement" recordNoun="harvest"
      orderBy={{ column: 'harvest_date', ascending: false }}
      recordLabel={(row) => `yield of ${row.harvest_code ?? 'harvest'}`}
      screenKey="plot.harvest"
      searchColumns={['harvest_code', 'plot_code', 'farmer_name', 'farmer_code', 'species_label']}
      secondary={{ sum: (row) => Number(row.yield_volume_m3 ?? 0), unit: 'm³', digits: 2 }}
      subtitle="Each harvest's yield: logs measured one by one in m³, or loads weighed in tonnes. Harvests are recorded on Tree inventory."
      table="ops.plot_harvest_entries" title="Yield statement" view="plot_harvest_entries"
      columns={[
        { key: 'harvest_code', label: 'Harvest', render: (row) => (
          <span className="cell-stack"><strong>{row.harvest_code ?? '--'}</strong><small className="muted">{formatDate(row.harvest_date)}</small></span>
        ) },
        { key: 'farmer_name', label: 'Farmer', render: (row) => (
          <span className="cell-stack"><strong>{row.farmer_name ?? '--'}</strong>
            <small className="muted">{[row.farmer_code, row.plot_code].filter(Boolean).join(' · ')}</small></span>
        ) },
        { key: 'species_label', label: 'Species', render: (row) => row.species_label ?? '--' },
        { key: 'trees_harvested', label: 'Trees', align: 'right', render: (row) => formatNumber(row.trees_harvested, 0) },
        { key: 'yield_basis', label: 'Measured', render: (row) => (row.yield_basis
          ? `${YIELD_BASES.find((entry) => entry.id === row.yield_basis)?.label ?? row.yield_basis}${row.yield_basis === 'log_volume'
            ? `, ${YIELD_FORMULAS.find((entry) => entry.id === row.yield_formula)?.label.split(':')[0] ?? ''}` : ''}`
          : <span className="field-error">not yet</span>) },
        { key: 'yield_log_count', label: 'Logs', align: 'right', render: (row) => (row.yield_basis === 'log_volume' ? formatNumber(row.yield_log_count, 0) : '--') },
        { key: 'yield_volume_m3', label: 'Volume (m³)', align: 'right', render: (row) => (row.yield_volume_m3 == null ? '--' : formatNumber(row.yield_volume_m3, 3)) },
        { key: 'yield_weight_t', label: 'Weight (t)', align: 'right', render: (row) => (row.yield_weight_t == null ? '--' : formatNumber(row.yield_weight_t, 3)) },
        { key: 'per_tree', label: 'Per tree', align: 'right', render: (row) => {
          if (!row.trees_harvested) return '--';
          if (row.yield_volume_m3 != null) return `${formatNumber(row.yield_volume_m3 / row.trees_harvested, 3)} m³`;
          if (row.yield_weight_t != null) return `${formatNumber(row.yield_weight_t / row.trees_harvested, 3)} t`;
          return '--';
        } },
        { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
      ]}
      stats={[
        { key: 'none', label: 'No yield recorded', tone: 'warn', match: (row) => !row.yield_basis || (row.yield_lines ?? []).length === 0 },
        { key: 'logs', label: 'Log by log (m³)', tone: 'info', match: (row) => row.yield_basis === 'log_volume' },
        { key: 'weight', label: 'By weight (t)', tone: 'info', match: (row) => row.yield_basis === 'weight' },
      ]}
      fields={yieldFields('Yield')}
      validate={(form) => ({
        yield_lines: form.yield_basis && Array.isArray(form.yield_lines)
          && !(form.yield_lines as Array<Record<string, string>>).some((line) => String(line.length_m ?? line.gross_t ?? '').trim() !== '')
          ? 'Add at least one log or load.' : undefined,
      })}
    />
  );
}
